import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { isGameMaster, getQuestStatusClass, cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Trash2,
  Loader2,
  Eye,
  EyeOff,
  Plus,
  X,
  GitBranch,
  Target,
  Unlock,
  Network,
  Zap,
} from 'lucide-react';
import { LoreLinksPanel } from '../components/lore/LoreLinksPanel';
import QuestActsPanel from '../components/quest/QuestActsPanel';
import { MentionTextarea } from '../components/mentions/MentionTextarea';
import { MentionText } from '../components/mentions/MentionText';
import { syncEntityMentions } from '../lib/mentions';
import { Link } from 'react-router-dom';

const API_URL = '/api';

const QUEST_STATUSES = ['unknown', 'known', 'in_progress', 'completed', 'failed', 'abandoned'];
const QUEST_TYPES = ['main', 'side', 'personal'];

export default function QuestDetailPage() {
  const { campaignId, questId } = useParams();
  const navigate = useNavigate();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();
  
  const [quest, setQuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [newObjective, setNewObjective] = useState('');
  const [narrativeContext, setNarrativeContext] = useState(null);

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  useEffect(() => {
    fetchQuest();
    fetchNarrativeContext();
  }, [questId]);

  const fetchQuest = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/quests/${questId}`);
      setQuest(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error(t('error_generic', language));
      navigate(`/campaigns/${campaignId}/quests`);
    } finally {
      setLoading(false);
    }
  };

  const fetchNarrativeContext = async () => {
    try {
      const response = await axios.get(`${API_URL}/quests/${questId}/narrative-context`);
      setNarrativeContext(response.data);
    } catch {
      // Silently fail – narrative context is optional
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.patch(`${API_URL}/quests/${questId}`, {
        title: editData.title,
        summary: editData.summary,
        quest_type: editData.quest_type,
        status: editData.status,
        visibility: editData.visibility,
        objectives: editData.objectives,
        rewards: editData.rewards,
        consequences: editData.consequences,
        hints: editData.hints,
        arc: editData.arc,
        act: editData.act,
      });
      setQuest(response.data);
      setEditMode(false);
      toast.success(t('success_saved', language));
      syncEntityMentions({
        campaignId,
        sourceType: 'quest',
        sourceId: questId,
        sourceFields: {
          summary: editData.summary || '',
          rewards: editData.rewards || '',
          consequences: editData.consequences || '',
          hints: (editData.hints || []).join('\n')
        }
      }).catch(() => {});
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(language === 'fr' ? 'Êtes-vous sûr de vouloir supprimer cette quête ?' : 'Are you sure you want to delete this quest?')) {
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/quests/${questId}`);
      toast.success(t('success_deleted', language));
      navigate(`/campaigns/${campaignId}/quests`);
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setDeleting(false);
    }
  };

  const addObjective = () => {
    if (!newObjective.trim()) return;
    setEditData(prev => ({
      ...prev,
      objectives: [...(prev.objectives || []), { text: newObjective, completed: false }]
    }));
    setNewObjective('');
  };

  const removeObjective = (index) => {
    setEditData(prev => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index)
    }));
  };

  const toggleObjective = (index) => {
    setEditData(prev => ({
      ...prev,
      objectives: prev.objectives.map((obj, i) => 
        i === index ? { ...obj, completed: !obj.completed } : obj
      )
    }));
  };

  if (loading || !quest) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 animate-fade-in" data-testid="quest-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(`/campaigns/${campaignId}/quests`)}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back', language)}
        </Button>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              {editMode ? (
                <Input
                  value={editData.title}
                  onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                  className="font-cinzel text-2xl font-bold"
                  data-testid="title-input"
                />
              ) : (
                <h1 className="font-cinzel text-3xl font-bold">{quest.title}</h1>
              )}
              <span className={cn("status-chip", getQuestStatusClass(quest.status))}>
                {t(`quest_status_${quest.status}`, language)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                {t(`quest_type_${quest.quest_type}`, language)}
              </span>
              {isMJ && (
                <span className={cn(
                  "text-xs px-2 py-0.5 rounded-full flex items-center gap-1",
                  quest.visibility === 'mj_only' ? 'bg-magic/10 text-magic' : 'bg-success/10 text-success'
                )}>
                  {quest.visibility === 'mj_only' ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  {t(`visibility_${quest.visibility}`, language)}
                </span>
              )}
            </div>
          </div>
          
          {isMJ && (
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <Button variant="outline" onClick={() => { setEditMode(false); setEditData(quest); }}>
                    {t('cancel', language)}
                  </Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    {t('save', language)}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-btn">
                    {t('edit', language)}
                  </Button>
                  <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="delete-btn">
                    {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                    {t('delete', language)}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Status & Type Selection in Edit Mode */}
        {editMode && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t('quest_status', language)}</Label>
              <Select value={editData.status} onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUEST_STATUSES.map(status => (
                    <SelectItem key={status} value={status}>
                      {t(`quest_status_${status}`, language)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t('quest_type', language)}</Label>
              <Select value={editData.quest_type} onValueChange={(v) => setEditData(prev => ({ ...prev, quest_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUEST_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      {t(`quest_type_${type}`, language)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t('visibility', language)}</Label>
              <Select value={editData.visibility} onValueChange={(v) => setEditData(prev => ({ ...prev, visibility: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mj_only">{t('visibility_mj_only', language)}</SelectItem>
                  <SelectItem value="players">{t('visibility_players', language)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Arc / Act in Edit Mode */}
        {editMode && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <GitBranch className="h-3.5 w-3.5 text-violet-500" />
                Arc
              </Label>
              <Input
                value={editData.arc || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, arc: e.target.value }))}
                placeholder={language === 'fr' ? 'Ex: La Corruption d\'Orcus' : 'Ex: The Corruption of Orcus'}
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Target className="h-3.5 w-3.5 text-violet-500" />
                {language === 'fr' ? 'Acte / Chapitre' : 'Act / Chapter'}
              </Label>
              <Input
                value={editData.act || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, act: e.target.value }))}
                placeholder={language === 'fr' ? 'Ex: Acte II' : 'Ex: Act II'}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>{t('quest_summary', language)}</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <MentionTextarea
                    campaignId={campaignId}
                    language={language}
                    value={editData.summary || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, summary: e.target.value }))}
                    rows={4}
                    data-testid="summary-input"
                  />
                ) : (
                  <MentionText
                    text={quest.summary || (language === 'fr' ? 'Pas de description.' : 'No description.')}
                    campaignId={campaignId}
                    language={language}
                  />
                )}
              </CardContent>
            </Card>

            {/* Objectives */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>{t('quest_objectives', language)}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {(editMode ? editData.objectives : quest.objectives)?.map((obj, i) => (
                    <li key={i} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30">
                      <Checkbox
                        checked={obj.completed}
                        onCheckedChange={() => editMode && toggleObjective(i)}
                        disabled={!editMode && !isMJ}
                        data-testid={`objective-${i}`}
                      />
                      <span className={cn(obj.completed && "line-through text-muted-foreground")}>
                        {obj.text}
                      </span>
                      {editMode && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="ml-auto h-6 w-6"
                          onClick={() => removeObjective(i)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </li>
                  ))}
                  {(!quest.objectives || quest.objectives.length === 0) && !editMode && (
                    <p className="text-muted-foreground">
                      {language === 'fr' ? 'Aucun objectif défini.' : 'No objectives defined.'}
                    </p>
                  )}
                </ul>
                
                {editMode && (
                  <div className="flex items-center gap-2 mt-4">
                    <Input
                      placeholder={language === 'fr' ? 'Nouvel objectif...' : 'New objective...'}
                      value={newObjective}
                      onChange={(e) => setNewObjective(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addObjective())}
                      data-testid="new-objective-input"
                    />
                    <Button variant="outline" onClick={addObjective} data-testid="add-objective-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Rewards */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>{t('quest_rewards', language)}</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <MentionTextarea
                    campaignId={campaignId}
                    language={language}
                    value={editData.rewards || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, rewards: e.target.value }))}
                    rows={3}
                    placeholder={language === 'fr' ? '500 PO, Épée magique +1...' : '500 GP, Magic Sword +1...'}
                    data-testid="rewards-input"
                  />
                ) : (
                  <MentionText
                    text={quest.rewards || (language === 'fr' ? 'Pas de récompenses définies.' : 'No rewards defined.')}
                    campaignId={campaignId}
                    language={language}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - MJ Only */}
          {isMJ && (
            <div className="space-y-6">
              {/* ── Position dans la campagne (Narrative Context) ── */}
              {(quest.arc || quest.act || narrativeContext?.triggered_by || narrativeContext?.linked_nodes?.length > 0 || narrativeContext?.unlocks?.length > 0) && (
                <Card className="glass-card border-violet-500/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <Network className="h-4 w-4 text-violet-500" />
                      {language === 'fr' ? 'Position dans la campagne' : 'Campaign Position'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Arc & Act */}
                    {(quest.arc || quest.act) && (
                      <div className="grid grid-cols-2 gap-2">
                        {quest.arc && (
                          <div className="bg-violet-500/5 border border-violet-500/15 rounded-lg p-2.5">
                            <p className="text-[10px] text-violet-400 font-semibold uppercase tracking-wider mb-0.5">Arc</p>
                            <p className="text-xs font-medium text-foreground">{quest.arc}</p>
                          </div>
                        )}
                        {quest.act && (
                          <div className="bg-violet-500/5 border border-violet-500/15 rounded-lg p-2.5">
                            <p className="text-[10px] text-violet-400 font-semibold uppercase tracking-wider mb-0.5">
                              {language === 'fr' ? 'Acte' : 'Act'}
                            </p>
                            <p className="text-xs font-medium text-foreground">{quest.act}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Triggered by node */}
                    {narrativeContext?.triggered_by && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                          <Zap className="h-3 w-3" />
                          {language === 'fr' ? 'Déclenchée par' : 'Triggered by'}
                        </p>
                        <Link
                          to={`/campaigns/${campaignId}/narrative`}
                          className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/5 border border-amber-500/15 hover:bg-amber-500/10 transition-colors text-xs text-foreground"
                        >
                          <Zap className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                          <span className="truncate font-medium">{narrativeContext.triggered_by.title}</span>
                        </Link>
                      </div>
                    )}

                    {/* Linked narrative nodes */}
                    {narrativeContext?.linked_nodes?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                          <GitBranch className="h-3 w-3" />
                          {language === 'fr' ? 'Nœuds narratifs liés' : 'Linked Narrative Nodes'}
                        </p>
                        <div className="space-y-1">
                          {narrativeContext.linked_nodes.map(node => (
                            <Link
                              key={node.id}
                              to={`/campaigns/${campaignId}/narrative`}
                              className="flex items-center gap-2 p-2 rounded-lg bg-violet-500/5 border border-violet-500/15 hover:bg-violet-500/10 transition-colors text-xs text-foreground"
                            >
                              <GitBranch className="h-3.5 w-3.5 text-violet-500 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <span className="truncate block font-medium">{node.title}</span>
                                {node.node_type && (
                                  <span className="text-[10px] text-muted-foreground capitalize">{node.node_type.replace(/_/g, ' ')}</span>
                                )}
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Unlocks nodes */}
                    {narrativeContext?.unlocks?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                          <Unlock className="h-3 w-3" />
                          {language === 'fr' ? 'Débloque' : 'Unlocks'}
                        </p>
                        <div className="space-y-1">
                          {narrativeContext.unlocks.map(node => (
                            <Link
                              key={node.id}
                              to={`/campaigns/${campaignId}/narrative`}
                              className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/15 hover:bg-emerald-500/10 transition-colors text-xs text-foreground"
                            >
                              <Unlock className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                              <span className="truncate font-medium">{node.title}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Consequences */}
              <Card className="glass-card border-magic/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <EyeOff className="h-4 w-4 text-magic" />
                    {t('quest_consequences', language)}
                  </CardTitle>
                  <CardDescription>
                    {language === 'fr' ? 'Visible uniquement par le MJ' : 'Visible only to GM'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {editMode ? (
                    <MentionTextarea
                      campaignId={campaignId}
                      language={language}
                      value={editData.consequences || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, consequences: e.target.value }))}
                      rows={4}
                      placeholder={language === 'fr'
                        ? 'Que se passe-t-il si réussie/échouée...'
                        : 'What happens if completed/failed...'}
                      data-testid="consequences-input"
                    />
                  ) : (
                    <MentionText
                      text={quest.consequences || (language === 'fr' ? 'Pas de conséquences définies.' : 'No consequences defined.')}
                      campaignId={campaignId}
                      language={language}
                      className="text-sm"
                    />
                  )}
                </CardContent>
              </Card>

              {/* Hints */}
              <Card className="glass-card border-magic/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <EyeOff className="h-4 w-4 text-magic" />
                    {t('quest_hints', language)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {editMode ? (
                    <MentionTextarea
                      campaignId={campaignId}
                      language={language}
                      value={(editData.hints || []).join('\n')}
                      onChange={(e) => setEditData(prev => ({
                        ...prev,
                        hints: e.target.value.split('\n').filter(l => l.trim())
                      }))}
                      rows={4}
                      placeholder={language === 'fr' ? 'Un indice par ligne... (@ pour mentionner)' : 'One hint per line... (@ to mention)'}
                      data-testid="hints-input"
                    />
                  ) : (
                    <ul className="space-y-2">
                      {quest.hints?.length > 0 ? quest.hints.map((hint, i) => (
                        <li key={i} className="text-sm flex items-start gap-2">
                          <span className="text-magic">•</span>
                          <MentionText text={hint} campaignId={campaignId} language={language} />
                        </li>
                      )) : (
                        <p className="text-sm text-muted-foreground">
                          {language === 'fr' ? 'Aucun indice.' : 'No hints.'}
                        </p>
                      )}
                    </ul>
                  )}
                </CardContent>
              </Card>

              {/* Lore Links Panel */}
              <LoreLinksPanel
                entityType="quest"
                entityId={questId}
                entityName={quest.title}
                campaignId={campaignId}
                isMJ={isMJ}
                data-testid="lore-links-panel"
              />
            </div>
          )}

          {/* Lore Links Panel for players (outside sidebar) */}
          {!isMJ && (
            <div className="lg:col-span-3">
              <LoreLinksPanel
                entityType="quest"
                entityId={questId}
                entityName={quest.title}
                campaignId={campaignId}
                isMJ={false}
                data-testid="lore-links-panel-player"
              />
            </div>
          )}
        </div>

        {/* ── Acts Panel — MJ only ─────────────────────────────────────── */}
        {isMJ && (
          <Card className="glass-card border-primary/20" data-testid="quest-acts-panel">
            <CardContent className="pt-5 pb-5">
              <QuestActsPanel questId={questId} language={language} />
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}

